// TODO(ui-foundation): promote this component to ui-foundation and remove from this repo.
import { useCallback, useState } from "react";
import {
  isMfaRestartRequiredError,
  type MfaDeliveryDetails,
} from "@its-engineering-and-development/jwt-auth-client";
import { AuthLinkButton } from "@app/components/atoms/auth-link-button";
import { AuthHeading } from "@app/components/molecules/auth-heading";
import { mapAuthError } from "@app/lib/auth-errors";
import { useJwtAuth } from "@app/providers/jwt-auth-provider";
import { Button, PinInput } from "@app/lib/ui-foundation";

function describeDestination(details: MfaDeliveryDetails | undefined): string {
  if (details?.destination) {
    return `Enter the 8-digit code we sent to ${details.destination}.`;
  }
  return "Check your email for an 8-digit code.";
}

export type MfaChallengeFormProps = {
  mfaTicket: string;
  deliveryDetails?: MfaDeliveryDetails;
  onRestartRequired: (reason: string) => void;
  onBack: () => void;
  headingAs?: "h1" | "h2";
};

export function MfaChallengeForm({
  mfaTicket,
  deliveryDetails,
  onRestartRequired,
  onBack,
  headingAs = "h2",
}: MfaChallengeFormProps) {
  const { verifyMfa } = useJwtAuth();
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const submitCode = useCallback(
    async (candidate: string) => {
      if (candidate.length !== 8) {
        return;
      }
      // Functional update lets us bail on re-entry without depending on the
      // isVerifying state value, so submitCode keeps a stable identity across
      // verify cycles (and PinInput's onChange handler doesn't re-mount).
      let alreadyVerifying = false;
      setIsVerifying((prev) => {
        alreadyVerifying = prev;
        return true;
      });
      if (alreadyVerifying) {
        return;
      }
      setErrorMessage("");
      try {
        await verifyMfa(mfaTicket, candidate);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[mfa] verify failed", error);
        }
        if (isMfaRestartRequiredError(error)) {
          onRestartRequired(
            "Your verification session expired. Please sign in again.",
          );
          return;
        }
        setCode("");
        setErrorMessage(
          mapAuthError(error, "That code didn't match. Please try again."),
        );
      } finally {
        setIsVerifying(false);
      }
    },
    [mfaTicket, onRestartRequired, verifyMfa],
  );

  const handleCodeChange = useCallback(
    (next: string) => {
      const cleaned = next.replace(/\D/g, "").slice(0, 8);
      setCode(cleaned);
      if (cleaned.length === 8) {
        void submitCode(cleaned);
      }
    },
    [submitCode],
  );

  return (
    <>
      <AuthHeading
        as={headingAs}
        title="Verify it's you"
        subtitle={describeDestination(deliveryDetails)}
      />

      <form
        className="events-form-stack"
        onSubmit={(event) => {
          event.preventDefault();
          void submitCode(code);
        }}
      >
        <div className="events-mfa-code">
          <PinInput size="md" disabled={isVerifying}>
            <PinInput.Group
              maxLength={8}
              value={code}
              onChange={handleCodeChange}
              autoComplete="one-time-code"
              inputMode="numeric"
              aria-label="8-digit verification code"
            >
              {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
                <PinInput.Slot key={index} index={index} />
              ))}
            </PinInput.Group>
          </PinInput>
        </div>

        <p className="events-auth-card__error" role="alert" aria-live="polite">
          {errorMessage}
        </p>

        <Button
          size="lg"
          color="primary"
          className="events-solid-button"
          type="submit"
          isDisabled={code.length !== 8 || isVerifying}
        >
          {isVerifying ? "Verifying…" : "Verify"}
        </Button>
      </form>

      <div className="events-auth-card__footer">
        <AuthLinkButton onClick={onBack}>Back to login</AuthLinkButton>
      </div>
    </>
  );
}
